/**
 * SupplierQuoteProgressTracker Component
 * 
 * Displays progress for quote requests per customer position
 * Shows visual progress through workflow states for each supplier quote type
 * Integrates with WorkflowStateManager for supplier case state tracking
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Button,
  Grid
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  RequestQuote as QuoteIcon,
  Build as BuildIcon,
  ContactMail as ContactMailIcon
} from '@mui/icons-material';

// Import WorkflowStateManager for supplier case progress
import { WorkflowStateManager } from '../shared/WorkflowStateManager';

interface CustomerPosition {
  id: string;
  position_number: number;
  part_name: string;
  part_number: string;
  quantity: number;
  type_specific_data: {
    selected_types?: string[];
    type_configurations?: Record<string, any>;
    material?: string;
    specifications?: string;
  };
}

interface SupplierQuoteInfo {
  supplier_case_id: string;
  supplier_case_number: string;
  supplier_name: string;
  quote_type: string;
  current_state: string;
  quoted_price: string | null;
  delivery_time_days: number | null;
  supplier_notes: string | null;
  has_selected_quote: boolean;
  selected_quote?: any;
}

interface PositionWithQuoteProgress {
  customer_position: CustomerPosition;
  supplier_quotes: SupplierQuoteInfo[];
  quote_progress: {
    total_types: number;
    quotes_requested: number;
    responses_received: number;
    quotes_selected: number;
  };
  completion_percentage: number;
}

interface SupplierQuoteProgressTrackerProps {
  customerCaseId: string;
  onRefresh?: () => void;
  showActions?: boolean;
}

const QUOTE_TYPE_LABELS: Record<string, string> = {
  'material': 'Material',
  'carbide': 'Hartmetall',
  'tool': 'Werkzeug',
  'consumable': 'Verbrauchsmaterial',
  'service': 'Dienstleistung'
};

const WORKFLOW_STATE_LABELS: Record<string, { label: string, color: string, icon: any }> = {
  'supplier_case_created': { 
    label: 'Erstellt', 
    color: '#757575', 
    icon: ScheduleIcon 
  },
  'supplier_quote_generated': { 
    label: 'Anfrage generiert', 
    color: '#1976d2', 
    icon: QuoteIcon 
  },
  'supplier_contacted': { 
    label: 'Lieferant kontaktiert', 
    color: '#ed6c02', 
    icon: ContactMailIcon 
  },
  'supplier_response_received': { 
    label: 'Antwort erhalten', 
    color: '#2e7d32', 
    icon: EmailIcon 
  },
  'quote_selected': { 
    label: 'Angebot ausgewählt', 
    color: '#388e3c', 
    icon: CheckCircle 
  },
  'converted_to_order': { 
    label: 'Bestellung erstellt', 
    color: '#4caf50', 
    icon: BuildIcon 
  }
};

export const SupplierQuoteProgressTracker: React.FC<SupplierQuoteProgressTrackerProps> = ({
  customerCaseId,
  onRefresh,
  showActions = true
}) => {
  const [progressData, setProgressData] = useState<PositionWithQuoteProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPositions, setExpandedPositions] = useState<Set<string>>(new Set());

  // Load supplier quote progress data
  const loadProgressData = async () => {
    setLoading(true);
    try {
      console.log('🔄 [PROGRESS-TRACKER] Loading supplier quote progress for customer case:', customerCaseId);
      
      // Step 1: Get supplier quote mappings
      const response = await fetch(`/api/v3/cases/${customerCaseId}/supplier-quote-mappings`);
      const result = await response.json();
      
      console.log('📊 [PROGRESS-TRACKER] Raw supplier quote mappings:', result);
      
      // Step 2: Get supplier case states and metadata 
      const supplierCasesResponse = await fetch(`/api/v3/cases/${customerCaseId}/supplier-cases`);
      const supplierCasesResult = await supplierCasesResponse.json();
      
      console.log('🏭 [PROGRESS-TRACKER] Supplier cases with metadata:', supplierCasesResult);
      
      if (result.success && result.mappings) {
        // Create supplier case metadata lookup
        const supplierCaseMetadata = new Map();
        if (supplierCasesResult.success && supplierCasesResult.supplier_cases) {
          supplierCasesResult.supplier_cases.forEach((supplierCase: any) => {
            const selectedQuote = supplierCase.metadata?.selected_quote;
            const quoteResponses = supplierCase.metadata?.quote_responses || [];
            
            // Enhanced selection detection: check both metadata and workflow state
            const hasSelectedQuote = selectedQuote || 
                                   quoteResponses.some((q: any) => q.selected || q.is_selected) ||
                                   supplierCase.workflow_state === 'quote_selected';
            
            supplierCaseMetadata.set(supplierCase.id, {
              workflow_state: supplierCase.workflow_state,
              has_selected_quote: hasSelectedQuote,
              selected_quote: selectedQuote,
              quote_responses: quoteResponses,
              case_number: supplierCase.case_number
            });
            
            console.log(`🔍 [PROGRESS-TRACKER] Supplier Case ${supplierCase.case_number}:`, {
              workflow_state: supplierCase.workflow_state,
              has_selected_quote: hasSelectedQuote,
              has_selected_quote_metadata: !!selectedQuote,
              has_selected_responses: quoteResponses.some((q: any) => q.selected || q.is_selected),
              workflow_state_selected: supplierCase.workflow_state === 'quote_selected',
              selected_quote_id: selectedQuote?.quote_id,
              responses_count: quoteResponses.length,
              responses_with_selection: quoteResponses.filter((q: any) => q.selected || q.is_selected).length
            });
          });
        }
        
        // Group mappings by customer position
        const positionGroups = new Map();
        
        result.mappings.forEach((mapping: any) => {
          const positionId = mapping.customer_quote_position_id;
          if (!positionGroups.has(positionId)) {
            positionGroups.set(positionId, []);
          }
          positionGroups.get(positionId).push(mapping);
        });
        
        // Get customer positions to create complete view
        const positionsResponse = await fetch(`/api/v3/cases/${customerCaseId}/positions`);
        const positionsResult = await positionsResponse.json();
        
        console.log('📋 [PROGRESS-TRACKER] Customer positions:', positionsResult);
        
        if (positionsResult.success && positionsResult.data) {
          const progressPositions: PositionWithQuoteProgress[] = positionsResult.data.map((position: any) => {
            const mappings = positionGroups.get(position.id) || [];
            const selectedTypes = position.type_specific_data?.selected_types || [];
            
            // Calculate progress metrics using actual supplier case metadata
            const quotesRequested = mappings.length;
            const responsesReceived = mappings.filter((m: any) => 
              m.quoted_price !== null && parseFloat(m.quoted_price) > 0
            ).length;
            
            // NEW: Check for quote selection in supplier case metadata
            const quotesSelected = mappings.filter((m: any) => {
              const supplierMeta = supplierCaseMetadata.get(m.supplier_case_id);
              return supplierMeta?.has_selected_quote || false;
            }).length;
            
            console.log(`📊 [PROGRESS-TRACKER] Position ${position.position_number} metrics:`, {
              selectedTypes: selectedTypes.length,
              quotesRequested,
              responsesReceived,
              quotesSelected,
              mappings: mappings.map((m: any) => ({
                supplier_case_id: m.supplier_case_id,
                supplier_case_number: m.supplier_case_number,
                has_selected_quote: supplierCaseMetadata.get(m.supplier_case_id)?.has_selected_quote || false,
                workflow_state: supplierCaseMetadata.get(m.supplier_case_id)?.workflow_state
              }))
            });
            
            const completionPercentage = selectedTypes.length > 0 
              ? (quotesSelected / selectedTypes.length) * 100 
              : 0;
            
            return {
              customer_position: position,
              supplier_quotes: mappings.map((mapping: any) => {
                const supplierMeta = supplierCaseMetadata.get(mapping.supplier_case_id);
                return {
                  supplier_case_id: mapping.supplier_case_id,
                  supplier_case_number: mapping.supplier_case_number,
                  supplier_name: mapping.supplier_name,
                  quote_type: extractQuoteTypeFromCase(mapping.supplier_case_number),
                  current_state: supplierMeta?.workflow_state || 'supplier_case_created',
                  quoted_price: mapping.quoted_price,
                  delivery_time_days: mapping.delivery_time_days,
                  supplier_notes: mapping.supplier_notes,
                  has_selected_quote: supplierMeta?.has_selected_quote || false,
                  selected_quote: supplierMeta?.selected_quote
                };
              }),
              quote_progress: {
                total_types: selectedTypes.length,
                quotes_requested: quotesRequested,
                responses_received: responsesReceived,
                quotes_selected: quotesSelected
              },
              completion_percentage: completionPercentage
            };
          });
          
          setProgressData(progressPositions);
          console.log('✅ [PROGRESS-TRACKER] Progress data processed with quote selection metadata:', {
            totalPositions: progressPositions.length,
            totalQuotesSelected: progressPositions.reduce((sum, p) => sum + p.quote_progress.quotes_selected, 0),
            positionDetails: progressPositions.map(p => ({
              position_number: p.customer_position.position_number,
              quotes_selected: p.quote_progress.quotes_selected,
              completion_percentage: p.completion_percentage
            }))
          });
        } else {
          setError('Failed to load customer positions');
        }
      } else {
        console.log('📭 [PROGRESS-TRACKER] No supplier quote mappings found');
        setProgressData([]);
      }
    } catch (error: any) {
      console.error('❌ [PROGRESS-TRACKER] Error loading progress data:', error);
      setError(error.message || 'Error loading progress data');
    } finally {
      setLoading(false);
    }
  };

  // Extract quote type from supplier case number (fallback)
  const extractQuoteTypeFromCase = (caseNumber: string): string => {
    if (caseNumber?.includes('CRB-')) return 'carbide';
    if (caseNumber?.includes('MAT-')) return 'material';
    if (caseNumber?.includes('TOL-')) return 'tool';
    return 'unknown';
  };

  // Handle accordion expansion
  const handlePositionExpansion = (positionId: string) => {
    const newExpanded = new Set(expandedPositions);
    if (newExpanded.has(positionId)) {
      newExpanded.delete(positionId);
    } else {
      newExpanded.add(positionId);
    }
    setExpandedPositions(newExpanded);
  };

  // Handle supplier case state transition
  const handleSupplierStateTransition = async (
    supplierCaseId: string, 
    fromState: string, 
    toState: string, 
    metadata?: any
  ) => {
    try {
      console.log(`🔄 [PROGRESS-TRACKER] Supplier state transition: ${supplierCaseId} ${fromState} → ${toState}`);
      
      // Call backend API to transition supplier case state
      const response = await fetch(`/api/v3/workflow/cases/${supplierCaseId}/transition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_state: toState,
          metadata: metadata
        })
      });
      
      const result = await response.json();
      if (result.success) {
        console.log('✅ [PROGRESS-TRACKER] Supplier state transition successful');
        // Refresh progress data
        await loadProgressData();
        if (onRefresh) onRefresh();
      } else {
        console.error('❌ [PROGRESS-TRACKER] Supplier state transition failed:', result.message);
      }
    } catch (error) {
      console.error('❌ [PROGRESS-TRACKER] Error in supplier state transition:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    if (customerCaseId) {
      loadProgressData();
    }
  }, [customerCaseId]);

  // Render quote type progress chip
  const renderQuoteTypeChip = (quoteType: string, state: string) => {
    const stateInfo = WORKFLOW_STATE_LABELS[state] || WORKFLOW_STATE_LABELS['supplier_case_created'];
    const StateIcon = stateInfo.icon;
    
    return (
      <Chip
        icon={<StateIcon />}
        label={`${QUOTE_TYPE_LABELS[quoteType] || quoteType}: ${stateInfo.label}`}
        sx={{
          backgroundColor: stateInfo.color,
          color: 'white',
          fontWeight: 'bold',
          mr: 1,
          mb: 1
        }}
        size="small"
      />
    );
  };

  // Render position progress summary
  const renderPositionSummary = (position: PositionWithQuoteProgress) => {
    const { customer_position, quote_progress, completion_percentage } = position;
    
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Position {customer_position.position_number}: {customer_position.part_name}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Teil-Nr: {customer_position.part_number} | Menge: {customer_position.quantity}
        </Typography>
        
        {/* Progress Bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ minWidth: 100 }}>
            Fortschritt: {Math.round(completion_percentage)}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={completion_percentage} 
            sx={{ 
              flexGrow: 1, 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: completion_percentage === 100 ? '#4caf50' : '#1976d2'
              }
            }}
          />
        </Box>
        
        {/* Progress Metrics */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">Typen erforderlich</Typography>
            <Typography variant="h6">{quote_progress.total_types}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">Anfragen gesendet</Typography>
            <Typography variant="h6">{quote_progress.quotes_requested}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">Antworten erhalten</Typography>
            <Typography variant="h6">{quote_progress.responses_received}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">Angebote ausgewählt</Typography>
            <Typography variant="h6">{quote_progress.quotes_selected}</Typography>
          </Grid>
        </Grid>
        
        {/* Quote Type Status Chips */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Status der Anfragetypen:</Typography>
          {(customer_position.type_specific_data?.selected_types || []).map((type) => {
            const quote = position.supplier_quotes.find(q => q.quote_type === type);
            const state = quote?.current_state || 'not_requested';
            
            if (state === 'not_requested') {
              return (
                <Chip
                  key={type}
                  label={`${QUOTE_TYPE_LABELS[type] || type}: Noch nicht angefragt`}
                  sx={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    mr: 1,
                    mb: 1
                  }}
                  size="small"
                />
              );
            }
            
            return renderQuoteTypeChip(type, state);
          })}
          
          {/* Show message if no quote types selected yet */}
          {(!customer_position.type_specific_data?.selected_types || customer_position.type_specific_data.selected_types.length === 0) && (
            <Chip
              label="Keine Anfragetypen konfiguriert - Konfiguration erforderlich"
              sx={{
                backgroundColor: '#ff9800',
                color: 'white',
                mr: 1,
                mb: 1
              }}
              size="small"
            />
          )}
        </Box>
      </Box>
    );
  };

  // Render supplier quote details table
  const renderSupplierQuoteTable = (position: PositionWithQuoteProgress) => {
    if (position.supplier_quotes.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          Keine Lieferantenanfragen für diese Position gefunden.
          {showActions && (
            <Button 
              variant="outlined" 
              size="small" 
              sx={{ mt: 1 }}
              onClick={() => console.log('Create supplier quotes for position:', position.customer_position.id)}
            >
              Lieferantenanfragen erstellen
            </Button>
          )}
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Typ</TableCell>
              <TableCell>Lieferant</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Ausgewählt</TableCell>
              <TableCell align="right">Einzelpreis</TableCell>
              <TableCell align="right">Gesamtpreis</TableCell>
              <TableCell align="right">Lieferzeit</TableCell>
              <TableCell align="center">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {position.supplier_quotes.map((quote) => {
              const stateInfo = WORKFLOW_STATE_LABELS[quote.current_state] || WORKFLOW_STATE_LABELS['supplier_case_created'];
              
              // Debug: Log pricing data availability
              console.log(`💰 [PRICE-DEBUG] Quote ${quote.supplier_case_number} pricing:`, {
                has_selected_quote: quote.has_selected_quote,
                selected_quote: quote.selected_quote,
                quoted_price: quote.quoted_price,
                supplier_case_id: quote.supplier_case_id
              });
              
              return (
                <TableRow key={quote.supplier_case_id}>
                  <TableCell>
                    <Chip 
                      label={QUOTE_TYPE_LABELS[quote.quote_type] || quote.quote_type}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {quote.supplier_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {quote.supplier_case_number}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={stateInfo.label}
                      sx={{
                        backgroundColor: stateInfo.color,
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {quote.has_selected_quote ? (
                      <Chip
                        label="AUSGEWÄHLT"
                        sx={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.7rem'
                        }}
                        size="small"
                        icon={<CheckCircle />}
                      />
                    ) : (
                      <Chip
                        label="Offen"
                        sx={{
                          backgroundColor: '#f5f5f5',
                          color: '#666',
                          fontSize: '0.7rem'
                        }}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {(() => {
                      // Priority: Use selected quote data if available, fallback to quoted_price
                      if (quote.has_selected_quote && quote.selected_quote?.quoted_positions?.length > 0) {
                        const position = quote.selected_quote.quoted_positions[0];
                        return `${parseFloat(position.unit_price || 0).toFixed(2)} EUR`;
                      }
                      if (quote.has_selected_quote && quote.selected_quote?.unit_price) {
                        return `${parseFloat(quote.selected_quote.unit_price).toFixed(2)} EUR`;
                      }
                      return quote.quoted_price ? `${parseFloat(quote.quoted_price).toFixed(2)} EUR` : '-';
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    {(() => {
                      // Priority: Use selected quote data if available, fallback to quoted_price
                      if (quote.has_selected_quote && quote.selected_quote?.total_quote_value) {
                        return `${parseFloat(quote.selected_quote.total_quote_value).toFixed(2)} EUR`;
                      }
                      return quote.quoted_price ? `${parseFloat(quote.quoted_price).toFixed(2)} EUR` : '-';
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    {(() => {
                      // Priority: Use selected quote data if available, fallback to delivery_time_days
                      if (quote.has_selected_quote && quote.selected_quote?.delivery_time_days) {
                        return `${quote.selected_quote.delivery_time_days} Tage`;
                      }
                      return quote.delivery_time_days ? `${quote.delivery_time_days} Tage` : '-';
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ minWidth: 200 }}>
                      <WorkflowStateManager
                        caseId={quote.supplier_case_id}
                        caseType="supplier_case"
                        currentState={quote.current_state}
                        onStateTransition={handleSupplierStateTransition}
                        showNextActions={showActions}
                        compactMode={true}
                        readonly={!showActions}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Lieferantenfortschritt wird geladen...
          </Typography>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Fehler beim Laden des Lieferantenfortschritts: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Lieferantenanfragen-Fortschritt
      </Typography>
      
      {progressData.length === 0 ? (
        <Alert severity="info">
          Keine Kundenpostionen gefunden.
        </Alert>
      ) : (
        progressData.map((position) => (
          <Accordion 
            key={position.customer_position.id}
            expanded={expandedPositions.has(position.customer_position.id)}
            onChange={() => handlePositionExpansion(position.customer_position.id)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              {renderPositionSummary(position)}
            </AccordionSummary>
            <AccordionDetails>
              {renderSupplierQuoteTable(position)}
            </AccordionDetails>
          </Accordion>
        ))
      )}
      
      {showActions && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button 
            variant="outlined" 
            onClick={loadProgressData}
            sx={{ mr: 2 }}
          >
            Aktualisieren
          </Button>
          {onRefresh && (
            <Button 
              variant="contained" 
              onClick={onRefresh}
            >
              Daten neu laden
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SupplierQuoteProgressTracker;
